import React, { useEffect, useRef, useState } from "react";
import {
  collection, collectionGroup, query, where,
  onSnapshot, doc, getDoc, setDoc, getDocs, updateDoc,
  addDoc, serverTimestamp, increment,
} from "firebase/firestore";
import { firestore } from "../shared/FirebaseConfig";
import { useUser } from "../shared/UserContext";
import { useClassroomMode } from "../shared/classroomMode";
import { findTeacherClass } from "../shared/classForStudent";
import { avatarColor, initials } from "../shared/avatar";
import { relativeWhen } from "../shared/dateLabels";
import "./ActiveSignOuts.css";

// ── Helpers ──────────────────────────────────────────────────────────────────

async function findStudent(idNum, uid, schoolId) {
  const ref = collection(firestore, "students");
  // Your own roster first…
  for (const q of [
    query(ref, where("studentId", "==", idNum), where("addedBy", "==", uid)),
    query(ref, where("studentId", "==", idNum), where("teachers", "array-contains", uid)),
  ]) {
    const snap = await getDocs(q);
    if (!snap.empty) return { docId: snap.docs[0].id, ...snap.docs[0].data() };
  }
  // …then the school directory (shared students), same as sign-out.
  if (schoolId) {
    const snap = await getDocs(
      query(ref, where("studentId", "==", idNum), where("schoolId", "==", schoolId))
    );
    const match = snap.docs.find(d => d.data().shared !== false);
    if (match) return { docId: match.id, ...match.data() };
  }
  return null;
}

async function lookupCatalog(uid, isbn) {
  const snap = await getDoc(doc(firestore, "users", uid, "libraryCatalog", isbn));
  if (!snap.exists()) return null;
  const d = snap.data();
  return { isbn, ...d, title: titleOf(d), author: authorOf(d) };
}

// Normalize a title/author for forgiving comparison — ignores case, surrounding
// space, and double spaces so "dog man" and " Dog  Man" match "Dog Man".
const norm = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");

// Read a book's title/author tolerant of older catalog docs that stored them
// under lending-record field names (bookTitle / fieldValues.Title) instead of
// the current `title` / `author`.
const titleOf  = (b) => b?.title  || b?.bookTitle  || b?.Title  || b?.fieldValues?.Title  || "";
const authorOf = (b) => b?.author || b?.bookAuthor || b?.Author || b?.fieldValues?.Author || "";

async function fetchBookByISBN(isbn) {
  // 1. Try Google Books
  try {
    const resp = await fetch(
      `https://www.googleapis.com/books/v1/volumes?q=isbn:${isbn}`
    );
    if (resp.ok) {
      const data = await resp.json();
      if (data.items?.length) {
        const info = data.items[0].volumeInfo;
        return {
          isbn,
          title:         info.title || "",
          author:        (info.authors || []).join(", "),
          publishedDate: (info.publishedDate || "").slice(0, 4),
          description:   info.description || "",
          thumbnail:     info.imageLinks?.thumbnail || "",
          pageCount:     info.pageCount || null,
          genre:         (info.categories || []).join(", "),
        };
      }
    }
  } catch (err) {
    console.warn("Google Books lookup failed:", err);
  }

  // 2. Fallback: Open Library (better international edition coverage)
  try {
    const resp = await fetch(
      `https://openlibrary.org/api/books?bibkeys=ISBN:${isbn}&format=json&jscmd=data`
    );
    if (resp.ok) {
      const data = await resp.json();
      const book = data[`ISBN:${isbn}`];
      if (book) {
        return {
          isbn,
          title:         book.title || "",
          author:        (book.authors || []).map(a => a.name).join(", "),
          publishedDate: book.publish_date || "",
          description:   book.notes?.value || book.notes || "",
          thumbnail:     book.cover?.medium || book.cover?.small || "",
          pageCount:     book.number_of_pages || null,
          genre:         (book.subjects || []).slice(0, 5).map(s => s.name).join(", "),
        };
      }
    }
  } catch (err) {
    console.warn("Open Library lookup failed:", err);
  }

  return null;
}

// Block obviously-inappropriate title searches before they ever hit the API.
// Word-boundary matched so real titles ("Essex", "Middlesex", "Sussex") are fine.
const BLOCKED_TERMS = [
  "sex", "sexy", "porn", "porno", "pornography", "nude", "nudes", "naked",
  "xxx", "erotic", "erotica", "kamasutra", "kama sutra", "playboy", "hentai",
  "nsfw", "fetish", "orgy", "bdsm", "milf", "incest", "rape",
];
const isBlockedQuery = (q) => {
  const s = norm(q);
  return BLOCKED_TERMS.some((t) => new RegExp(`(^|\\W)${t}(\\W|$)`).test(s));
};

// Title search for adding a NEW book (no ISBN to scan). Tries Google Books first
// (it gives us a MATURE flag to filter on), then falls back to Open Library, which
// is keyless and has no daily quota — so a Google rate-limit (HTTP 429) doesn't
// kill the feature. Returns { books, error } so the UI can tell "none found" from
// "service unavailable".
async function searchBooksByTitle(query) {
  // 1) Google Books
  try {
    const resp = await fetch(
      `https://www.googleapis.com/books/v1/volumes?q=${encodeURIComponent("intitle:" + query)}&maxResults=12&printType=books&orderBy=relevance`
    );
    if (resp.ok) {
      const data = await resp.json();
      const books = (data.items || [])
        .filter((it) => it.volumeInfo?.maturityRating !== "MATURE")
        .map((it) => {
          const info = it.volumeInfo || {};
          const ids = info.industryIdentifiers || [];
          const isbn =
            ids.find((i) => i.type === "ISBN_13")?.identifier ||
            ids.find((i) => i.type === "ISBN_10")?.identifier ||
            it.id; // fall back to the volume id so the catalog key stays unique
          return {
            isbn,
            title: info.title || "",
            author: (info.authors || []).join(", "),
            publishedDate: (info.publishedDate || "").slice(0, 4),
            description: info.description || "",
            thumbnail: info.imageLinks?.thumbnail || "",
            pageCount: info.pageCount || null,
            genre: (info.categories || []).join(", "),
          };
        })
        .filter((b) => b.title);
      if (books.length) return { books };
      // Google responded but found nothing — fall through to Open Library.
    }
  } catch (e) {
    console.warn("Google Books title search failed:", e);
  }

  // 2) Open Library fallback
  try {
    const resp = await fetch(
      `https://openlibrary.org/search.json?title=${encodeURIComponent(query)}&limit=12&fields=title,author_name,first_publish_year,isbn,cover_i,cover_edition_key,key`
    );
    if (resp.ok) {
      const data = await resp.json();
      const books = (data.docs || [])
        .map((d) => ({
          isbn: (d.isbn || [])[0] || (d.cover_edition_key ? `OL:${d.cover_edition_key}` : `OL${d.key}`),
          title: d.title || "",
          author: (d.author_name || []).join(", "),
          publishedDate: d.first_publish_year ? String(d.first_publish_year) : "",
          description: "",
          thumbnail: d.cover_i ? `https://covers.openlibrary.org/b/id/${d.cover_i}-M.jpg` : "",
          pageCount: null,
          genre: "",
        }))
        .filter((b) => b.title);
      return { books };
    }
  } catch (e) {
    console.warn("Open Library title search failed:", e);
  }

  return { books: [], error: true };
}

// ── Checkout Section ─────────────────────────────────────────────────────────

function CheckoutSection({ uid, schoolId }) {
  const [studentId, setStudentId]       = useState("");
  const [studentData, setStudentData]   = useState(null);
  const [studentError, setStudentError] = useState("");

  const [isbn, setIsbn]                 = useState("");
  const [bookInfo, setBookInfo]         = useState(null);
  // source: null | "catalog" | "api" | "manual" | "return"
  const [bookSource, setBookSource]     = useState(null);
  const [activeLoan, setActiveLoan]     = useState(null); // loan chosen to return
  const [returnOptions, setReturnOptions] = useState([]); // all active loans for the scanned ISBN
  const [bookLookingUp, setBookLookingUp] = useState(false);
  const [bookError, setBookError]       = useState("");

  const [manualTitle, setManualTitle]   = useState("");
  const [manualAuthor, setManualAuthor] = useState("");

  const [submitting, setSubmitting]     = useState(false);
  const [success, setSuccess]           = useState("");

  const isbnRef = useRef(null);

  // Full catalog for the title typeahead — lets you check a known book out by name
  // (no scanner) and steers manual entries toward an existing title (no dup/typo).
  const [catalog, setCatalog]       = useState([]);
  const [onlineResults, setOnlineResults]   = useState([]);
  const [onlineSearching, setOnlineSearching] = useState(false);
  const [onlineMsg, setOnlineMsg]           = useState("");

  useEffect(() => {
    if (!uid) return;
    getDocs(collection(firestore, "users", uid, "libraryCatalog"))
      .then(snap => setCatalog(snap.docs.map(d => {
        const data = d.data();
        return { isbn: d.id, ...data, title: titleOf(data), author: authorOf(data) };
      })))
      .catch(e => console.warn("Catalog load for search failed:", e));
  }, [uid]);

  const resetBook = () => {
    setBookInfo(null); setBookSource(null); setActiveLoan(null); setReturnOptions([]);
    setBookError(""); setManualTitle(""); setManualAuthor("");
    setOnlineResults([]); setOnlineMsg("");
  };

  // Switch from the "this book is out" state into checking out another copy.
  const checkoutAnotherCopy = () => {
    setActiveLoan(null); setReturnOptions([]); setBookSource("catalog");
  };

  // Back out of a looked-up book without checking out / returning.
  const cancelBook = () => {
    setIsbn(""); resetBook();
  };

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(""), 4000);
  };

  const handleStudentKey = async (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    setStudentError("");
    const student = await findStudent(studentId.trim(), uid, schoolId);
    if (!student) { setStudentError("Student not found."); return; }
    setStudentData(student);
    isbnRef.current?.focus();
  };

  // One smart action for the unified Book field: digits → scan/ISBN lookup,
  // anything else → search online by title to add a new book.
  const smartBookSearch = () => {
    const q = isbn.trim();
    if (!q) return;
    const digits = q.replace(/[-\s]/g, "");
    if (/^\d{8,}$/.test(digits)) lookupBook(q);
    else runOnlineTitleSearch();
  };

  const handleIsbnKey = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    smartBookSearch();
  };

  const lookupBook = async (rawArg) => {
    // rawArg is a string when called from the title search; ignore the click event
    // that the Search button's onClick would otherwise pass in.
    const raw = (typeof rawArg === "string" ? rawArg : isbn).trim();
    if (!raw) return;
    setBookLookingUp(true); setBookError("");
    setBookInfo(null); setBookSource(null); setActiveLoan(null);

    try {
      // First: find ALL active loans of this book (any class, by teacherUID) so
      // multiple copies out to different students can each be returned. Isolated
      // try/catch so a transient/index error doesn't kill the whole lookup.
      let matches = [];
      try {
        const loanSnap = await getDocs(
          query(collectionGroup(firestore, "lendingRecords"), where("teacherUID", "==", uid))
        );
        matches = loanSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(r => r.trackerId === "library" && r.isbn === raw && r.time_in === null);
      } catch (e) {
        console.warn("Active-loan pre-check failed; proceeding with normal lookup:", e);
      }

      if (matches.length > 0) {
        // Book is out (one or more copies) — show return UI.
        const cached = await lookupCatalog(uid, raw);
        setBookInfo(cached || { isbn: raw, title: matches[0].bookTitle, author: matches[0].bookAuthor });
        setReturnOptions(matches);
        setActiveLoan(matches.length === 1 ? matches[0] : null); // auto-select if only one
        setBookSource("return");
      } else {
        // Book is available — normal checkout lookup
        const cached = await lookupCatalog(uid, raw);
        if (cached) {
          setBookInfo(cached); setBookSource("catalog");
        } else {
          const apiBook = await fetchBookByISBN(raw);
          if (apiBook) {
            setBookInfo(apiBook); setBookSource("api");
          } else {
            setBookSource("manual");
          }
        }
      }
    } catch {
      setBookError("Lookup failed. Try again.");
    } finally {
      setBookLookingUp(false);
    }
  };

  // Pick a book from the title search (or the duplicate nudge) — behaves exactly
  // like scanning its ISBN, so it also detects if that copy is currently out.
  const selectCatalogBook = (book) => {
    setIsbn(book.isbn);
    lookupBook(book.isbn);
  };

  // Add a NEW book by title (no scanner). Blocks inappropriate searches up front;
  // the search helper also drops anything Google flags MATURE.
  const runOnlineTitleSearch = async () => {
    const q = isbn.trim();
    if (!q) return;
    setOnlineMsg(""); setOnlineResults([]);
    if (isBlockedQuery(q)) {
      setOnlineMsg("That search isn't allowed.");
      return;
    }
    setOnlineSearching(true);
    try {
      const { books, error } = await searchBooksByTitle(q);
      setOnlineResults(books);
      if (error) setOnlineMsg("Book search is temporarily unavailable — try again, or scan/type the ISBN.");
      else if (books.length === 0) setOnlineMsg("No books found for that title.");
    } catch {
      setOnlineMsg("Search failed. Try again.");
    } finally {
      setOnlineSearching(false);
    }
  };

  const selectGoogleBook = (r) => {
    setOnlineResults([]); setOnlineMsg("");
    setIsbn(r.isbn);
    setBookInfo(r); setBookSource("api");
    setActiveLoan(null); setReturnOptions([]); setBookError("");
  };

  const handleReturn = async () => {
    if (!activeLoan) return;
    setSubmitting(true);
    try {
      await updateDoc(
        doc(firestore, `classes/${activeLoan.classId}/lendingRecords/${activeLoan.id}`),
        { time_in: new Date().toISOString() }
      );
      showSuccess(`✓ "${bookInfo?.title}" returned by ${activeLoan.studentName}`);
      setIsbn(""); resetBook();
    } catch (err) {
      console.error("Return error:", err);
      setBookError("Return failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheckout = async () => {
    let student = studentData;
    if (!student) {
      if (!studentId.trim()) { setStudentError("Enter a student ID first."); return; }
      student = await findStudent(studentId.trim(), uid, schoolId);
      if (!student) { setStudentError("Student not found. Check the ID and try again."); return; }
      setStudentData(student);
    }

    const finalBook = bookSource === "manual"
      ? { isbn, title: manualTitle.trim(), author: manualAuthor.trim(), publishedDate: "", description: "", thumbnail: "", pageCount: null, genre: "" }
      : bookInfo;

    if (!finalBook?.title) { setBookError("Enter a book title."); return; }

    setSubmitting(true);
    try {
      if (bookSource !== "catalog") {
        await setDoc(
          doc(firestore, "users", uid, "libraryCatalog", finalBook.isbn),
          { ...finalBook, addedAt: serverTimestamp() }
        );
        // Keep the title search current so a just-added book is reusable immediately.
        setCatalog(prev => [...prev.filter(b => b.isbn !== finalBook.isbn), finalBook]);
      }

      // Attribute the checkout to the class THIS teacher has the student in
      // (their own roster class), not the student's first enrolled class.
      const cls = await findTeacherClass(student, uid);
      if (!cls) { setBookError("Student has no enrolled class."); return; }
      const classId = cls.id;
      const classData = cls;

      await addDoc(collection(firestore, `classes/${classId}/lendingRecords`), {
        trackerId:             "library",
        trackerName:           "Library",
        studentDocumentNumber: student.docId,
        studentName:           student.studentName,
        studentId:             studentId.trim(),
        isbn:                  finalBook.isbn,
        bookTitle:             finalBook.title,
        bookAuthor:            finalBook.author,
        fieldValues:           { Title: finalBook.title, Author: finalBook.author },
        time_out:              new Date().toISOString(),
        time_in:               null,
        teacherUID:            uid,
        classId,
        className:             classData.className ?? "",
      });

      showSuccess(`✓ "${finalBook.title}" checked out to ${student.studentName}`);
      setStudentId(""); setStudentData(null);
      setIsbn(""); resetBook();
    } catch (err) {
      console.error("Checkout error:", err);
      setBookError("Checkout failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Live matches from YOUR catalog as you type in the unified Book field. Matched
  // on title, author, or ISBN so typing a title or scanning a known ISBN both hit.
  const q = norm(isbn);
  const titleMatches = q
    ? catalog
        .filter(b => norm(b.title).includes(q) || norm(b.author).includes(q) || (b.isbn || "").includes(q))
        .sort((a, b) => (a.title || "").localeCompare(b.title || ""))
        .slice(0, 8)
    : [];

  // When typing a manual title, surface an existing catalog book it matches so the
  // teacher reuses that exact spelling instead of creating a near-duplicate.
  const manualDup = bookSource === "manual" && manualTitle.trim()
    ? catalog.find(b => norm(b.title) === norm(manualTitle))
    : null;

  return (
    <div className="form-container">
      <h2>Check Out / Return</h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

        {/* Student ID — only needed for checkout, not return */}
        {bookSource !== "return" && (
          <div>
            <input
              type="text"
              placeholder="Student ID — press Enter to confirm"
              value={studentId}
              onChange={e => { setStudentId(e.target.value); setStudentData(null); setStudentError(""); }}
              onKeyDown={handleStudentKey}
              style={{ width: "100%", boxSizing: "border-box" }}
            />
            {studentError && <p style={{ color: "var(--color-error)", fontSize: 13, margin: "4px 0 0" }}>{studentError}</p>}
            {studentData && (
              <p style={{ color: "green", fontSize: 13, margin: "4px 0 0" }}>✓ {studentData.studentName}</p>
            )}
          </div>
        )}

        {/* Book — scan a barcode, or type a title / ISBN to find it in your library or add a new one */}
        {bookSource !== "return" && (
          <div style={{ position: "relative" }}>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                ref={isbnRef}
                type="text"
                placeholder="Scan barcode, or type a book title or ISBN"
                value={isbn}
                onChange={e => { setIsbn(e.target.value); resetBook(); }}
                onKeyDown={handleIsbnKey}
                style={{ flex: 1, boxSizing: "border-box" }}
              />
              <button
                type="button"
                onClick={smartBookSearch}
                disabled={bookLookingUp || onlineSearching || !isbn.trim()}
                style={{ whiteSpace: "nowrap" }}
              >
                {(bookLookingUp || onlineSearching) ? "Searching…" : "Search"}
              </button>
            </div>
            {bookLookingUp && <p style={{ fontSize: 13, color: "var(--color-secondary)", margin: "4px 0 0" }}>Looking up…</p>}
            {bookError && <p style={{ color: "var(--color-error)", fontSize: 13, margin: "4px 0 0" }}>{bookError}</p>}

            {/* Live matches from YOUR library as you type */}
            {!bookSource && titleMatches.length > 0 && (
              <div style={{
                position: "absolute", zIndex: 5, left: 0, right: 0, top: "100%",
                background: "#fff", border: "1px solid #ddd", borderRadius: "0 0 8px 8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)", maxHeight: 240, overflowY: "auto",
              }}>
                {titleMatches.map(b => (
                  <button
                    key={b.isbn}
                    type="button"
                    onClick={() => selectCatalogBook(b)}
                    style={{
                      display: "block", width: "100%", textAlign: "left", padding: "8px 12px",
                      background: "none", border: "none", borderBottom: "1px solid #f0f0f0",
                      cursor: "pointer", fontSize: 14, color: "var(--color-primary)",
                    }}
                  >
                    {b.title || <span style={{ color: "var(--color-secondary)" }}>ISBN {b.isbn}</span>}
                    {b.author && <span style={{ color: "var(--color-secondary)" }}> · {b.author}</span>}
                  </button>
                ))}
              </div>
            )}

            {/* Not in your library → look it up online to add it */}
            {!bookSource && isbn.trim() && titleMatches.length === 0 && !bookLookingUp && onlineResults.length === 0 && (
              <p style={{ fontSize: 12, color: "var(--color-secondary)", margin: "6px 0 0" }}>
                {onlineMsg || "Not in your library — press Search to find it online and add it."}
              </p>
            )}

            {/* Online results to add a new book */}
            {onlineResults.length > 0 && (
              <div style={{ border: "1px solid #ddd", borderRadius: 8, marginTop: 8, maxHeight: 320, overflowY: "auto" }}>
                {onlineResults.map(r => (
                  <button
                    key={r.isbn}
                    type="button"
                    onClick={() => selectGoogleBook(r)}
                    style={{
                      display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
                      padding: "8px 12px", background: "none", border: "none",
                      borderBottom: "1px solid #f0f0f0", cursor: "pointer", color: "var(--color-primary)",
                    }}
                  >
                    {r.thumbnail
                      ? <img src={r.thumbnail} alt="" style={{ width: 32, height: 46, objectFit: "cover", borderRadius: 3, flexShrink: 0 }} />
                      : <span style={{ width: 32, height: 46, background: "#eef", borderRadius: 3, flexShrink: 0 }} />}
                    <span style={{ fontSize: 14 }}>
                      <strong>{r.title}</strong>
                      {r.author && <span style={{ color: "var(--color-secondary)" }}> · {r.author}</span>}
                      {r.publishedDate && <span style={{ color: "var(--color-secondary)" }}> · {r.publishedDate}</span>}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Return card — book is currently checked out (one or more copies) */}
        {bookSource === "return" && bookInfo && (
          <div style={{ padding: "14px 16px", background: "#fff8e1", border: "1px solid #f59e0b", borderRadius: 8 }}>
            <p style={{ margin: "0 0 2px", fontWeight: 600 }}>{bookInfo.title}</p>
            {bookInfo.author && <p style={{ margin: "0 0 8px", fontSize: 13, color: "var(--color-secondary)" }}>{bookInfo.author}</p>}

            {returnOptions.length > 1 && !activeLoan ? (
              <>
                <p style={{ margin: "0 0 8px", fontSize: 14 }}>
                  {returnOptions.length} copies out — who's returning?
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                  {returnOptions.map(loan => (
                    <button
                      key={loan.id}
                      onClick={() => setActiveLoan(loan)}
                      style={{ padding: "6px 14px", fontSize: 14 }}
                    >
                      {loan.studentName}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <p style={{ margin: "0 0 12px", fontSize: 14 }}>
                Checked out by <strong>{activeLoan?.studentName}</strong>
              </p>
            )}

            {activeLoan && (
              <button onClick={handleReturn} disabled={submitting}>
                {submitting ? "Returning…" : `Return ${activeLoan.studentName}'s copy`}
              </button>
            )}
            <button
              onClick={checkoutAnotherCopy}
              disabled={submitting}
              style={{ marginLeft: activeLoan ? 8 : 0, background: "none", border: "1px solid #ddd", color: "var(--color-secondary)" }}
            >
              Check out another copy
            </button>
            <button
              onClick={cancelBook}
              disabled={submitting}
              style={{ marginLeft: 8, background: "none", border: "1px solid #ddd", color: "var(--color-secondary)" }}
            >
              Cancel
            </button>
          </div>
        )}

        {/* Book preview — available for checkout */}
        {(bookSource === "catalog" || bookSource === "api") && bookInfo && (
          <div style={{ padding: "12px 16px", background: "#f0f7ff", border: "1px solid #c8dff7", borderRadius: 8 }}>
            <p style={{ margin: 0, fontWeight: 600 }}>{bookInfo.title}</p>
            {bookInfo.author && <p style={{ margin: "2px 0 0", fontSize: 13, color: "var(--color-secondary)" }}>{bookInfo.author}{bookInfo.publishedDate ? ` · ${bookInfo.publishedDate}` : ""}</p>}
            {bookSource === "api" && <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--color-secondary)" }}>Found via Google Books — will be saved to your library.</p>}
          </div>
        )}

        {/* Manual entry */}
        {bookSource === "manual" && (
          <div style={{ padding: "12px 16px", background: "#fffbf0", border: "1px solid #f0e0a0", borderRadius: 8 }}>
            <p style={{ margin: "0 0 8px", fontSize: 13, color: "var(--color-secondary)" }}>Book not found — enter details manually:</p>
            {manualDup && (
              <div style={{ margin: "0 0 8px", padding: "8px 10px", background: "#f0f7ff", border: "1px solid #c8dff7", borderRadius: 6, fontSize: 13 }}>
                Already in your library as <strong>"{manualDup.title}"</strong>.{" "}
                <button
                  type="button"
                  onClick={() => selectCatalogBook(manualDup)}
                  style={{ padding: "2px 8px", fontSize: 13 }}
                >
                  Use that instead
                </button>
              </div>
            )}
            <input
              type="text" placeholder="Title" value={manualTitle}
              onChange={e => setManualTitle(e.target.value)}
              style={{ width: "100%", boxSizing: "border-box", marginBottom: 6 }}
            />
            <input
              type="text" placeholder="Author (optional)" value={manualAuthor}
              onChange={e => setManualAuthor(e.target.value)}
              style={{ width: "100%", boxSizing: "border-box" }}
            />
          </div>
        )}

        {success && <p style={{ color: "green", fontWeight: 500, fontSize: 14 }}>{success}</p>}

        {(bookSource === "catalog" || bookSource === "api" || bookSource === "manual") && (
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleCheckout} disabled={submitting}>
              {submitting ? "Checking out…" : "Check Out"}
            </button>
            <button
              onClick={cancelBook}
              disabled={submitting}
              style={{ background: "none", border: "1px solid #ddd", color: "var(--color-secondary)" }}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Active Checkouts Table ───────────────────────────────────────────────────

const OVERDUE_MS = 14 * 24 * 60 * 60 * 1000; // books out longer than 14 days

// Pops after a book is checked back in: a kid rates it (⭐ 1–5) and says whether
// they'd recommend it (👍/👎). Both optional — "Skip" never blocks the return. The
// result aggregates onto the catalog card (avg stars + a "Recommended" badge).
function BookRatingModal({ title, onSubmit, onClose }) {
  const [stars, setStars]         = useState(0);
  const [hover, setHover]         = useState(0);
  const [recommend, setRecommend] = useState(null); // "up" | "down" | null

  return (
    <div className="rating-overlay" role="dialog" aria-modal="true" aria-label="Rate this book">
      <div className="rating-card">
        <button className="rating-close" onClick={onClose} aria-label="Skip">×</button>
        <h2 className="rating-title">How was it?</h2>
        {title && <p className="rating-book">{title}</p>}

        <div className="rating-stars" onMouseLeave={() => setHover(0)}>
          {[1, 2, 3, 4, 5].map(n => (
            <button
              key={n}
              type="button"
              className={`rating-star${(hover || stars) >= n ? " is-on" : ""}`}
              onMouseEnter={() => setHover(n)}
              onClick={() => setStars(s => (s === n ? 0 : n))}
              aria-label={`${n} star${n > 1 ? "s" : ""}`}
            >★</button>
          ))}
        </div>

        <p className="rating-q">Would you recommend it?</p>
        <div className="rating-thumbs">
          <button
            type="button"
            className={`rating-thumb${recommend === "up" ? " is-up" : ""}`}
            onClick={() => setRecommend(r => (r === "up" ? null : "up"))}
          >👍 Yes</button>
          <button
            type="button"
            className={`rating-thumb${recommend === "down" ? " is-down" : ""}`}
            onClick={() => setRecommend(r => (r === "down" ? null : "down"))}
          >👎 No</button>
        </div>

        <div className="rating-actions">
          <button type="button" className="rating-skip" onClick={onClose}>Skip</button>
          <button
            type="button"
            className="rating-submit"
            disabled={!stars && !recommend}
            onClick={() => onSubmit({ stars, recommend })}
          >Save rating</button>
        </div>
      </div>
    </div>
  );
}

function ActiveCheckoutsTable({ uid }) {
  // Reuse the teacher's existing Classroom Mode PIN as the manual-return override
  // code, so there's a single code to remember. If they never set one, the manual
  // return falls back to a plain confirm (so it never blocks them).
  const { classroomPin } = useUser();
  const [loans, setLoans] = useState([]);
  const [returningId, setReturningId] = useState(null); // loan.id currently in code-entry
  const [codeEntry, setCodeEntry]     = useState("");
  const [codeError, setCodeError]     = useState("");
  const [submitting, setSubmitting]   = useState(false);
  const [ratingFor, setRatingFor]     = useState(null); // loan just returned → rating modal

  useEffect(() => {
    if (!uid) return;
    // Read all library loans this teacher created, regardless of which class the
    // record is nested under (covers shared students whose home class is another
    // teacher's). The record stamps teacherUID + className.
    return onSnapshot(
      query(collectionGroup(firestore, "lendingRecords"), where("teacherUID", "==", uid)),
      snap => {
        const active = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(r => r.trackerId === "library" && r.time_in === null);
        setLoans(active);
      }
    );
  }, [uid]);

  const startReturn = (loanId) => {
    setReturningId(loanId); setCodeEntry(""); setCodeError("");
  };
  const cancelReturn = () => {
    setReturningId(null); setCodeEntry(""); setCodeError("");
  };

  // Manual return — no scanner needed. Click a kid's card, enter the override code,
  // and the book is checked back in (mirrors signing a student back in by hand).
  const confirmReturn = async (loan) => {
    if (classroomPin && codeEntry !== classroomPin) {
      setCodeError("Incorrect code.");
      return;
    }
    setSubmitting(true);
    try {
      await updateDoc(
        doc(firestore, `classes/${loan.classId}/lendingRecords/${loan.id}`),
        { time_in: new Date().toISOString() }
      );
      cancelReturn(); // the snapshot drops the returned book from the list automatically
      setRatingFor(loan); // ask the student to rate the book they just returned
    } catch (err) {
      console.error("Return error:", err);
      setCodeError("Return failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Save a rating: stamp it on the loan record (history) and roll it into the
  // catalog book's aggregate (avg stars + recommend tally) shown on the Catalog card.
  // Owner-only writes (own lendingRecords + own libraryCatalog) — no rules change.
  const saveRating = async (loan, { stars, recommend }) => {
    setRatingFor(null);
    if (!loan || (!stars && !recommend)) return;
    try {
      await updateDoc(
        doc(firestore, `classes/${loan.classId}/lendingRecords/${loan.id}`),
        { rating: stars || null, recommend: recommend || null }
      ).catch(() => {});
      if (loan.isbn) {
        const agg = {};
        if (stars) { agg.ratingCount = increment(1); agg.ratingSum = increment(stars); }
        if (recommend === "up")   agg.recUp = increment(1);
        if (recommend === "down") agg.recDown = increment(1);
        await setDoc(doc(firestore, "users", uid, "libraryCatalog", loan.isbn), agg, { merge: true });
      }
    } catch (err) {
      console.error("Rating save failed:", err);
    }
  };

  return (
    <div className="display-sign-out-container">
      <div className="signout-header">
        <h2 style={{ margin: 0 }}>
          Currently Out
          {loans.length > 0 && <span className="signout-count">{loans.length} out</span>}
        </h2>
      </div>

      {loans.length === 0 ? (
        <div className="signout-empty">No books currently checked out.</div>
      ) : (
        <div className="signout-list">
          {[...loans].sort((a, b) => new Date(a.time_out) - new Date(b.time_out)).map(loan => {
            const sub = [loan.bookTitle, loan.bookAuthor, loan.className].filter(Boolean).join(" · ");
            const overdue = Date.now() - new Date(loan.time_out) > OVERDUE_MS;
            const isReturning = returningId === loan.id;
            return (
              <div key={loan.id} className="signout-card">
                <span className="signout-avatar" style={{ background: avatarColor(loan.studentName) }}>
                  {initials(loan.studentName)}
                </span>
                <span className="signout-body">
                  <span className="signout-name">{loan.studentName}</span>
                  {sub && <span className="signout-sub">{sub}</span>}
                </span>

                {isReturning ? (
                  <span style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    {classroomPin ? (
                      <input
                        type="password"
                        inputMode="numeric"
                        autoFocus
                        maxLength={4}
                        placeholder="Code"
                        value={codeEntry}
                        onChange={e => { setCodeEntry(e.target.value.replace(/\D/g, "")); setCodeError(""); }}
                        onKeyDown={e => { if (e.key === "Enter") confirmReturn(loan); if (e.key === "Escape") cancelReturn(); }}
                        style={{ width: 64, textAlign: "center", letterSpacing: 2, padding: "5px 6px", boxSizing: "border-box" }}
                      />
                    ) : (
                      <span style={{ fontSize: 13, color: "var(--color-secondary)" }}>Return this book?</span>
                    )}
                    <button onClick={() => confirmReturn(loan)} disabled={submitting} style={{ padding: "5px 12px", fontSize: 14 }}>
                      {submitting ? "…" : "Check in"}
                    </button>
                    <button
                      onClick={cancelReturn}
                      disabled={submitting}
                      style={{ padding: "5px 10px", fontSize: 14, background: "none", border: "1px solid #ddd", color: "var(--color-secondary)" }}
                    >
                      Cancel
                    </button>
                    {codeError && <span style={{ color: "var(--color-error)", fontSize: 12, width: "100%", textAlign: "right" }}>{codeError}</span>}
                  </span>
                ) : (
                  <>
                    {overdue && <span className="overdue-pill">Overdue</span>}
                    <span className={`card-when ${overdue ? "is-overdue" : ""}`}>Out {relativeWhen(loan.time_out)}</span>
                    <span className="signout-action">
                      <button className="signout-return-btn" onClick={() => startReturn(loan.id)}>
                        Return
                      </button>
                    </span>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {ratingFor && (
        <BookRatingModal
          title={ratingFor.bookTitle}
          onSubmit={(r) => saveRating(ratingFor, r)}
          onClose={() => setRatingFor(null)}
        />
      )}
    </div>
  );
}

// ── Rapid "scan book after book into the catalog" panel ──────────────────────
// Lives at the top of the Catalog tab. A barcode scanner types the ISBN then hits
// Enter, so each scan auto-looks-up + auto-adds and re-focuses the box for the next
// one — no clicking between books. Typing a title instead runs a search with per-
// result Add buttons.
function AddBooksPanel({ uid, existingIsbns, onAdd }) {
  const [value, setValue]   = useState("");
  const [busy, setBusy]     = useState(false);
  const [msg, setMsg]       = useState(null);      // { ok, text }
  const [results, setResults] = useState(null);    // title-search candidates
  const [added, setAdded]   = useState([]);        // this session: [{ isbn, title }]
  const inputRef = useRef(null);
  const focusInput = () => inputRef.current?.focus();

  const saveBook = async (book) => {
    if (!book?.isbn || !book.title) return false;
    if (existingIsbns.has(book.isbn) || added.some(a => a.isbn === book.isbn)) {
      setMsg({ ok: false, text: `“${book.title}” is already in your catalog.` });
      return false;
    }
    await setDoc(doc(firestore, "users", uid, "libraryCatalog", book.isbn), { ...book, addedAt: serverTimestamp() });
    onAdd(book);
    setAdded(prev => [{ isbn: book.isbn, title: book.title }, ...prev]);
    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const q = value.trim();
    if (!q || busy) return;
    setBusy(true); setMsg(null); setResults(null);
    try {
      const cleaned = q.replace(/[-\s]/g, "");
      const isIsbn = cleaned.length >= 10 && /^\d+[\dxX]$/.test(cleaned);
      if (isIsbn) {
        const book = await fetchBookByISBN(cleaned);
        if (book?.title) {
          const ok = await saveBook(book);
          if (ok) setMsg({ ok: true, text: `Added “${book.title}”.` });
          setValue("");
        } else {
          setMsg({ ok: false, text: `No book found for ${cleaned}. Try typing the title instead.` });
        }
      } else {
        const { books, error } = await searchBooksByTitle(q);
        if (error) setMsg({ ok: false, text: error });
        else if (!books.length) setMsg({ ok: false, text: `No matches for “${q}”.` });
        else setResults(books);
      }
    } catch (err) {
      console.error("Add book failed:", err);
      setMsg({ ok: false, text: "Something went wrong. Try again." });
    } finally {
      setBusy(false);
      focusInput();
    }
  };

  const addFromResult = async (book) => {
    try {
      const ok = await saveBook(book);
      if (ok) { setMsg({ ok: true, text: `Added “${book.title}”.` }); setResults(null); setValue(""); }
    } catch (err) {
      console.error("Add book failed:", err);
      setMsg({ ok: false, text: "Couldn't add that one. Try again." });
    }
    focusInput();
  };

  return (
    <div className="lib-add-panel">
      <div className="lib-add-head">
        <strong>➕ Add books to your catalog</strong>
        <span>Scan a barcode and press Enter — keep scanning to add book after book. Or type a title / ISBN and search.</span>
      </div>
      <form className="lib-add-form" onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          type="text"
          autoFocus
          value={value}
          onChange={e => { setValue(e.target.value); setMsg(null); }}
          placeholder="Scan barcode, or type a book title / ISBN…"
        />
        <button type="submit" disabled={busy || !value.trim()}>
          {busy ? "…" : "Add"}
        </button>
      </form>

      {msg && (
        <p className={`lib-add-msg ${msg.ok ? "is-ok" : "is-error"}`}>
          {msg.ok ? "✓ " : "⚠ "}{msg.text}
        </p>
      )}

      {results && (
        <div className="lib-add-results">
          {results.map(b => (
            <div key={b.isbn} className="lib-add-result">
              {b.thumbnail
                ? <img src={b.thumbnail} alt="" className="lib-add-cover" />
                : <span className="lib-add-cover lib-add-cover--blank">📕</span>}
              <span className="lib-add-result-body">
                <span className="lib-add-result-title">{b.title}</span>
                <span className="lib-add-result-sub">{[b.author, b.publishedDate].filter(Boolean).join(" · ")}</span>
              </span>
              <button
                type="button"
                className="signout-return-btn"
                onClick={() => addFromResult(b)}
                disabled={existingIsbns.has(b.isbn) || added.some(a => a.isbn === b.isbn)}
              >
                {existingIsbns.has(b.isbn) || added.some(a => a.isbn === b.isbn) ? "Added" : "Add"}
              </button>
            </div>
          ))}
        </div>
      )}

      {added.length > 0 && (
        <div className="lib-add-session">
          <span className="lib-add-session-label">Added this session ({added.length}):</span>
          {added.map(a => <span key={a.isbn} className="lib-add-chip">{a.title}</span>)}
        </div>
      )}
    </div>
  );
}

// ── Catalog Browser ──────────────────────────────────────────────────────────

function CatalogBrowser({ uid }) {
  const [books, setBooks]       = useState([]);
  const [activeLoans, setActiveLoans] = useState({}); // isbn → { studentName }
  const [search, setSearch]     = useState("");
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    if (!uid) return;

    const loadCatalog = async () => {
      try {
        // Fetch catalog
        const catSnap = await getDocs(collection(firestore, "users", uid, "libraryCatalog"));
        const allBooks = catSnap.docs
          .map(d => {
            const data = d.data();
            return { isbn: d.id, ...data, title: titleOf(data), author: authorOf(data) };
          })
          .sort((a, b) => (a.title || "").localeCompare(b.title || ""));
        setBooks(allBooks);

        // Active loans — isolated so an index/transient error here can't blank the catalog.
        try {
          const loanSnap = await getDocs(
            query(collectionGroup(firestore, "lendingRecords"), where("teacherUID", "==", uid))
          );
          const loansMap = {};
          loanSnap.docs
            .map(d => d.data())
            .filter(r => r.trackerId === "library" && r.time_in === null)
            .forEach(r => { loansMap[r.isbn] = { studentName: r.studentName }; });
          setActiveLoans(loansMap);
        } catch (e) {
          console.warn("Catalog active-loans lookup failed (catalog still shown):", e);
        }
      } catch (e) {
        console.error("Error loading catalog:", e);
      } finally {
        setLoading(false);
      }
    };

    loadCatalog();
  }, [uid]);

  const filtered = books.filter(b => {
    const q = search.toLowerCase();
    return !q
      || b.title?.toLowerCase().includes(q)
      || b.author?.toLowerCase().includes(q)
      || b.genre?.toLowerCase().includes(q);
  });

  if (loading) return <div style={{ padding: 20, color: "var(--color-secondary)" }}>Loading catalog…</div>;

  const existingIsbns = new Set(books.map(b => b.isbn));
  // A just-added book is merged into the live catalog + kept sorted, so it appears
  // in the list immediately (and the search typeahead) without a refetch.
  const handleAdd = (book) => setBooks(prev =>
    [...prev.filter(b => b.isbn !== book.isbn), { ...book, title: titleOf(book) || book.title, author: authorOf(book) || book.author }]
      .sort((a, b) => (a.title || "").localeCompare(b.title || ""))
  );

  return (
    <div className="display-sign-out-container">
      <AddBooksPanel uid={uid} existingIsbns={existingIsbns} onAdd={handleAdd} />

      {books.length === 0 ? (
        <div className="signout-empty">
          Your catalog is empty — scan a barcode above to add your first book.
          (Books are also added automatically when you check one out.)
        </div>
      ) : (
      <>
      <div className="signout-header">
        <h2 style={{ margin: 0 }}>
          Catalog
          <span className="signout-count">{books.length} book{books.length !== 1 ? "s" : ""}</span>
        </h2>
        <input
          type="text"
          className="signout-search"
          placeholder="Search by title, author, or genre…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #ddd", fontSize: 14 }}
        />
      </div>

      {filtered.length === 0 ? (
        <div className="signout-empty">No books match "{search}"</div>
      ) : (
        <div className="signout-list">
          {filtered.map(book => {
            const loan = activeLoans[book.isbn];
            // Genre gets its own pill (below); the subline keeps author · year.
            const meta = [book.author, book.publishedDate].filter(Boolean).join(" · ");
            const genre = (book.genre || "").split(",")[0].trim();
            // Aggregate rating from student check-in ratings (see BookRatingModal).
            const avg = book.ratingCount ? book.ratingSum / book.ratingCount : 0;
            const recTotal = (book.recUp || 0) + (book.recDown || 0);
            const recommended = recTotal >= 2 && (book.recUp || 0) / recTotal >= 0.6;
            return (
              <div key={book.isbn} className="signout-card">
                <span className="signout-avatar" style={{ background: avatarColor(book.title || book.isbn) }}>
                  {initials(book.title || "?")}
                </span>
                <span className="signout-body">
                  <span className="signout-name">
                    {book.title}
                    {genre && (
                      <span
                        style={{ marginLeft: 8, padding: "1px 8px", borderRadius: 999, background: "#eef2f6", color: "var(--color-primary)", fontSize: 11, fontWeight: 600, verticalAlign: "middle", whiteSpace: "nowrap" }}
                      >
                        {genre}
                      </span>
                    )}
                  </span>
                  {meta && <span className="signout-sub">{meta}</span>}
                  {(book.ratingCount > 0 || recommended) && (
                    <span className="book-rating">
                      {book.ratingCount > 0 && (
                        <span className="book-stars">★ {avg.toFixed(1)} <span className="book-rating-count">({book.ratingCount})</span></span>
                      )}
                      {recommended && <span className="rec-pill">👍 Recommended</span>}
                    </span>
                  )}
                </span>
                {loan ? (
                  <span className="signout-loc" style={{ background: "#fff4e0", color: "#a35a00" }}>
                    Out — {loan.studentName}
                  </span>
                ) : (
                  <span className="signout-loc" style={{ background: "#e7f6ec", color: "#15703a" }}>
                    Available
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
      </>
      )}
    </div>
  );
}

// ── History (circulation log) ────────────────────────────────────────────────

function HistoryTab({ uid }) {
  const [records, setRecords] = useState(null); // null = loading
  const [mode, setMode]       = useState("book"); // "book" | "student"
  const [search, setSearch]   = useState("");
  const [openKey, setOpenKey] = useState(null);

  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    (async () => {
      try {
        // Every library loan this teacher made — completed AND active. Same query
        // the live tables use; just not filtered to time_in === null.
        const snap = await getDocs(
          query(collectionGroup(firestore, "lendingRecords"), where("teacherUID", "==", uid))
        );
        const recs = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(r => r.trackerId === "library");
        if (!cancelled) setRecords(recs);
      } catch (e) {
        console.error("History load failed:", e);
        if (!cancelled) setRecords([]);
      }
    })();
    return () => { cancelled = true; };
  }, [uid]);

  if (records === null) return <div style={{ padding: 20, color: "var(--color-secondary)" }}>Loading history…</div>;

  if (records.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "var(--color-secondary)" }}>
        <p>No checkout history yet.</p>
        <p style={{ fontSize: 14 }}>Once books are checked out and returned, the full record shows here.</p>
      </div>
    );
  }

  const recTitle = (r) => titleOf(r) || (r.isbn ? `ISBN ${r.isbn}` : "Unknown book");

  // Group every record by book or by student.
  const groups = {};
  records.forEach(r => {
    const key = mode === "book"
      ? (r.isbn || recTitle(r))
      : (r.studentDocumentNumber || r.studentId || r.studentName || r.id);
    if (!groups[key]) {
      groups[key] = {
        key,
        title: mode === "book" ? recTitle(r) : (r.studentName || "Unknown student"),
        sub:   mode === "book" ? authorOf(r) : "",
        records: [],
      };
    }
    groups[key].records.push(r);
  });

  const q = search.trim().toLowerCase();
  const list = Object.values(groups)
    .map(g => ({
      ...g,
      records: [...g.records].sort((a, b) => new Date(b.time_out) - new Date(a.time_out)),
      lastOut: Math.max(...g.records.map(r => new Date(r.time_out).getTime() || 0)),
    }))
    .filter(g => !q
      || g.title.toLowerCase().includes(q)
      || (g.sub || "").toLowerCase().includes(q)
      || g.records.some(r => (mode === "book" ? (r.studentName || "") : recTitle(r)).toLowerCase().includes(q)))
    .sort((a, b) => b.lastOut - a.lastOut);

  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null;

  return (
    <div className="display-sign-out-container">
      <div className="signout-header" style={{ flexWrap: "wrap", gap: 12 }}>
        <h2 style={{ margin: 0 }}>History</h2>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", flex: "1 1 auto", minWidth: 0 }}>
          <div style={{ display: "flex", border: "1px solid #ddd", borderRadius: 6, overflow: "hidden" }}>
            {[["book", "By book"], ["student", "By student"]].map(([id, label]) => (
              <button
                key={id}
                onClick={() => { setMode(id); setOpenKey(null); }}
                style={{
                  padding: "6px 14px", fontSize: 14, border: "none", cursor: "pointer",
                  background: mode === id ? "var(--color-primary)" : "#fff",
                  color: mode === id ? "#fff" : "var(--color-secondary)",
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <input
            type="text"
            placeholder={mode === "book" ? "Search books / students…" : "Search students / books…"}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #ddd", flex: "1 1 180px", minWidth: 0, fontSize: 14 }}
          />
        </div>
      </div>

      {list.length === 0 ? (
        <div className="signout-empty">No matches for "{search}"</div>
      ) : (
        <div className="signout-list">
          {list.map(g => {
            const isOpen = openKey === g.key;
            const outNow = g.records.filter(r => r.time_in === null).length;
            return (
              <div key={g.key} style={{ border: "1px solid #eee", borderRadius: 8, marginBottom: 8, overflow: "hidden" }}>
                <button
                  onClick={() => setOpenKey(isOpen ? null : g.key)}
                  style={{
                    display: "flex", alignItems: "center", gap: 12, width: "100%",
                    padding: "10px 14px", background: "none", border: "none", cursor: "pointer", textAlign: "left",
                  }}
                >
                  <span className="signout-avatar" style={{ background: avatarColor(g.title) }}>{initials(g.title)}</span>
                  <span className="signout-body" style={{ flex: 1 }}>
                    <span className="signout-name">{g.title}</span>
                    {g.sub && <span className="signout-sub">{g.sub}</span>}
                  </span>
                  {outNow > 0 && (
                    <span className="signout-loc" style={{ background: "#fff4e0", color: "#a35a00" }}>{outNow} out now</span>
                  )}
                  <span className="signout-sub">{g.records.length} checkout{g.records.length !== 1 ? "s" : ""}</span>
                  <span style={{ color: "var(--color-secondary)" }}>{isOpen ? "▾" : "▸"}</span>
                </button>
                {isOpen && (
                  <div style={{ borderTop: "1px solid #f0f0f0" }}>
                    {g.records.map(r => (
                      <div
                        key={r.id}
                        style={{
                          display: "flex", justifyContent: "space-between", gap: 12,
                          padding: "8px 14px 8px 52px", fontSize: 14, borderBottom: "1px solid #f6f6f6",
                        }}
                      >
                        <span>{mode === "book" ? (r.studentName || "Unknown student") : recTitle(r)}</span>
                        <span style={{ color: "var(--color-secondary)", whiteSpace: "nowrap" }}>
                          {fmt(r.time_out)} → {r.time_in ? fmt(r.time_in) : <span style={{ color: "#a35a00" }}>still out</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

const TABS = [
  { id: "checkout", label: "Check Out" },
  { id: "catalog",  label: "Catalog"   },
  { id: "history",  label: "History"   },
];

export default function LibraryPage() {
  const { uid, schoolId } = useUser();
  const { classroomMode } = useClassroomMode();
  const [libraryEnabled, setLibraryEnabled] = useState(null); // null = loading
  const [activeTab, setActiveTab]           = useState("checkout");

  // History is a management/browse view — hide it on a shared/student device so
  // kids can't page through the circulation log. Fall back off it if it's open.
  const visibleTabs = classroomMode ? TABS.filter(t => t.id !== "history") : TABS;
  useEffect(() => {
    if (classroomMode && activeTab === "history") setActiveTab("checkout");
  }, [classroomMode, activeTab]);

  useEffect(() => {
    if (!uid) return;
    return onSnapshot(doc(firestore, "users", uid), snap => {
      setLibraryEnabled(snap.data()?.libraryEnabled === true);
    });
  }, [uid]);

  if (libraryEnabled === null) return <div>Loading…</div>;

  if (!libraryEnabled) {
    return (
      <div className="content-container" style={{ textAlign: "center", paddingTop: 60 }}>
        <h2>Library</h2>
        <p style={{ color: "var(--color-secondary)", marginBottom: 24 }}>
          Enable the library feature in <strong>Settings</strong> to start tracking book checkouts
          and building your catalog.
        </p>
      </div>
    );
  }

  return (
    <div className="content-container">
      {/* Tab bar */}
      <div style={{ display: "flex", gap: 0, borderBottom: "2px solid #eee", marginBottom: 24 }}>
        {visibleTabs.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            style={{
              background: "none", border: "none",
              borderBottom: t.id === activeTab ? "2px solid var(--color-primary)" : "2px solid transparent",
              marginBottom: -2,
              padding: "10px 20px",
              fontWeight: t.id === activeTab ? 600 : 400,
              color: t.id === activeTab ? "var(--color-primary)" : "var(--color-secondary)",
              cursor: "pointer", fontSize: 15,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "checkout" && (
        <div className="split-layout">
          <div className="split-form">
            <CheckoutSection uid={uid} schoolId={schoolId} />
          </div>
          <div className="split-list">
            <ActiveCheckoutsTable uid={uid} />
          </div>
        </div>
      )}
      {activeTab === "catalog" && <CatalogBrowser uid={uid} />}
      {activeTab === "history" && !classroomMode && <HistoryTab uid={uid} />}
    </div>
  );
}
